// Sound for the viewer's Record button. Each sound system (world-game sound
// effects, trivia, world music) plays through its own AudioContext, so
// instead of one shared bus each registers a tap: its context and the node
// everything it plays passes through. While a recording runs, every tap is
// copied into one mixing context whose single audio track goes into the
// video. Systems that start making sound after recording began (contexts
// are created on first use) are picked up within a second.

type Tap = () => { ctx: AudioContext; node: AudioNode } | null;

const taps = new Set<Tap>();

/** Register a sound system. Returns an unregister. */
export function registerRecordingTap(tap: Tap): () => void {
  taps.add(tap);
  return () => { taps.delete(tap); };
}

export interface RecordingAudio {
  /** The mixed track to add to the MediaRecorder stream (null if audio isn't available). */
  track: MediaStreamTrack | null;
  stop: () => void;
}

/** Start mixing every registered sound system into one track. Call from a user gesture. */
export function startRecordingAudio(): RecordingAudio {
  const AC = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext | undefined;
  if (!AC) return { track: null, stop: () => {} };
  let mix: AudioContext;
  try { mix = new AC(); } catch { return { track: null, stop: () => {} }; }
  void mix.resume().catch(() => {});
  const out = mix.createMediaStreamDestination();
  const linked = new Map<AudioNode, () => void>();

  const link = () => {
    for (const tap of taps) {
      let t: ReturnType<Tap> = null;
      try { t = tap(); } catch { t = null; }
      if (!t || linked.has(t.node) || t.ctx.state === 'closed') continue;
      try {
        const bridge = t.ctx.createMediaStreamDestination();
        t.node.connect(bridge);
        const src = mix.createMediaStreamSource(bridge.stream);
        src.connect(out);
        const node = t.node;
        linked.set(node, () => { try { node.disconnect(bridge); } catch { /* already gone */ } src.disconnect(); });
      } catch { /* this system can't be tapped (e.g. Safari quirks); record without it */ }
    }
  };
  link();
  const timer = window.setInterval(link, 1000);

  return {
    track: out.stream.getAudioTracks()[0] ?? null,
    stop: () => {
      window.clearInterval(timer);
      for (const undo of linked.values()) undo();
      linked.clear();
      void mix.close().catch(() => {});
    },
  };
}
